<template>
  <v-form>
    <v-text-field
      v-model="form.name"
      :error-messages="getErrors('form.name')"
      :label="$t('global.id')"
      @input="$v.form.name.$touch()"
      @blur="$v.form.name.$touch()"
      filled
      style="width: 330px"
    ></v-text-field>
    <v-select
      :items="detectionPoints"
      :error-messages="getErrors('form.eventDetectionPointId')"
      :label="$t('global.adminEventCode.detectionPoint')"
      item-text="name"
      item-value="id"
      v-model="form.eventDetectionPointId"
      @input="$v.form.eventDetectionPointId.$touch()"
      @blur="$v.form.eventDetectionPointId.$touch()"
      filled
      style="width: 330px"
    ></v-select>
    <v-select
      :items="errorSeverities"
      :error-messages="getErrors('form.severity')"
      :label="$t('global.adminEventCode.severity')"
      v-model="form.severity"
      @input="$v.form.severity.$touch()"
      @blur="$v.form.severity.$touch()"
      filled
      style="width: 330px"
    ></v-select>
    <v-textarea
      v-for="lang of allowedLanguages"
      :key="lang"
      v-model="form.descriptionTranslations[lang]"
      :error-messages="getErrors(`form.descriptionTranslations.${lang}`)"
      @input="$v.form.descriptionTranslations[lang].$touch()"
      @blur="$v.form.descriptionTranslations[lang].$touch()"
      filled
      style="width: 600px"
      counter="255"
      rows="2"
    >
      <template v-slot:label>
        {{ $t('global.adminEventCode.description') }}
        <v-icon class="ml-1" style="vertical-align: middle">{{ `$${lang}` }}</v-icon>
      </template>
    </v-textarea>
    <v-select
      :items="notificationClasses"
      :error-messages="getErrors('form.defaultNotificationClass')"
      :label="$t('global.defaultNotificationClass')"
      v-model="form.defaultNotificationClass"
      @input="$v.form.defaultNotificationClass.$touch()"
      @blur="$v.form.defaultNotificationClass.$touch()"
      filled
      style="width: 330px"
    >
      <template v-slot:selection="{ item }">
        <NotificationClass class="mt-2" :notificationClass="item" />
      </template>
      <template v-slot:item="{ active, item, attrs, on }">
        <v-list-item v-on="on" v-bind="attrs" #default="{ active }">
          <NotificationClass :notificationClass="item" />
        </v-list-item>
      </template>
    </v-select>
    <v-textarea
      v-model="form.detectionMechanism"
      :label="$t('global.adminEventCode.detectionMechanism')"
      :error-messages="getErrors('form.detectionMechanism')"
      @input="$v.form.detectionMechanism.$touch()"
      @blur="$v.form.detectionMechanism.$touch()"
      filled
      style="width: 330px"
      counter="255"
    ></v-textarea>
    <v-textarea
      v-model="form.terminationMechanism"
      :label="$t('global.adminEventCode.terminationMechanism')"
      :error-messages="getErrors('form.terminationMechanism')"
      @input="$v.form.terminationMechanism.$touch()"
      @blur="$v.form.terminationMechanism.$touch()"
      filled
      style="width: 330px"
      counter="255"
    ></v-textarea>
    <v-textarea
      v-model="form.actionOnEvent"
      :label="$t('global.adminEventCode.actionOnEvent')"
      :error-messages="getErrors('form.actionOnEvent')"
      @input="$v.form.actionOnEvent.$touch()"
      @blur="$v.form.actionOnEvent.$touch()"
      filled
      style="width: 330px"
      counter="255"
    ></v-textarea>
    <!-- <v-checkbox
      class="mt-3"
      v-model="form.disabled === 'DEACTIVATED'"
      :label="$t('global.adminEventCode.disabled')"
    ></v-checkbox> -->

    <v-select
      :items="activationStatusItems"
      :label="$t('global.adminEventCode.disabled')"
      item-text="text"
      item-value="value"
      v-model="form.disabled"
      @input="$v.form.disabled.$touch()"
      @blur="$v.form.disabled.$touch()"
      filled
      style="width: 330px"
    ></v-select>

    <!-- Test Event Notification Button -->
    <v-btn color="warning" outlined class="mt-4" @click="showTestDialog = true">
      <v-icon left>mdi-test-tube</v-icon>
      Test Event Notification
    </v-btn>

    <!-- Test Event Notification Dialog -->
    <v-dialog v-model="showTestDialog" max-width="500px">
      <v-card>
        <v-card-title>
          <span class="headline">Test Event Notification</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="testForm" v-model="testFormValid">
            <v-text-field
              v-model="testForm.eventCode"
              label="Event Code"
              :rules="[(v) => !!v || 'Event Code is required']"
              required
              filled
              hint="Enter the event code to test"
            ></v-text-field>

            <v-autocomplete
              v-model="testForm.selectedDevice"
              :items="searchDevices || []"
              :search-input.sync="deviceSearchText"
              item-text="name"
              item-value="id"
              label="Select Device"
              :rules="[(v) => !!v || 'Device is required']"
              required
              filled
              hint="Search and select a device to send notification to"
              clearable
              no-data-text="Type to search for devices..."
              :loading="$apollo.queries.searchDevices.loading"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.name }}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{ item.company?.name }} • {{ item.controller }}
                    <v-chip
                      x-small
                      :color="item.connectionStatus ? 'success' : 'error'"
                      class="ml-2"
                    >
                      {{ item.connectionStatus ? 'Online' : 'Offline' }}
                    </v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey darken-1" text @click="showTestDialog = false"> Cancel </v-btn>
          <v-btn
            color="warning"
            :disabled="!testFormValid || testLoading"
            :loading="testLoading"
            @click="triggerTestNotification"
          >
            Trigger Notification
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success/Error Snackbar -->
    <v-snackbar v-model="showSnackbar" :color="snackbarColor" timeout="5000" top>
      {{ snackbarMessage }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="showSnackbar = false"> Close </v-btn>
      </template>
    </v-snackbar>
  </v-form>
</template>
<script>
import { validationMixin } from 'vuelidate'
import vuelidateErrorsExtractor from '@/mixins/vuelidateErrorsExtractor'
import { required, maxLength } from 'vuelidate/lib/validators'
import { eventDetectionPointsAdmin } from '@/graphql/query/eventDetectionPointsAdmin'
import { notificationClassesEnumValues } from '@/graphql/query/notificationClassesEnumValues'
import { searchDevices } from '@/graphql/query/device'
import { errorsConfig } from '@/config/errorsConfig'
import { allowedLanguages } from '@/translations'
import NotificationClass from '@/components/NotificationDispatcher/NotificationClass'
import { eventDeactivationState } from '../../../config/eventDeactivation'
import { triggerEventNotification } from '@/graphql/mutations/triggerEventNotification'

export default {
  name: 'EventCodeForm',
  mixins: [validationMixin, vuelidateErrorsExtractor],
  components: { NotificationClass },
  props: {
    value: Object,
    eventCode: Object,
  },
  data() {
    return {
      form: {
        name: null,
        eventDetectionPointId: null,
        severity: null,
        // TODO: CHANGED TYPE
        disabled: 'DEACTIVATED',
        defaultNotificationClass: null,
        detectionMechanism: null,
        terminationMechanism: null,
        actionOnEvent: null,
        descriptionTranslations: Object.fromEntries(allowedLanguages.map((lang) => [lang, null])),
      },
      // Test notification dialog
      showTestDialog: false,
      testFormValid: false,
      testLoading: false,
      testForm: {
        eventCode: this.eventCode?.name || '',
        deviceId: '',
        selectedDevice: null,
      },
      deviceSearchText: '',
      // Snackbar for feedback
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',
    }
  },
  validations: {
    form: {
      name: { required },
      eventDetectionPointId: { required },
      severity: { required },
      disabled: { required },
      defaultNotificationClass: { required },
      detectionMechanism: { maxLength: maxLength(255) },
      terminationMechanism: { maxLength: maxLength(255) },
      actionOnEvent: { maxLength: maxLength(255) },
      descriptionTranslations: Object.fromEntries(
        allowedLanguages.map((lang) => [lang, { maxLength: maxLength(255) }]),
      ),
    },
  },
  apollo: {
    detectionPoints: {
      query: eventDetectionPointsAdmin,
      update(data) {
        return data.eventDetectionPointsAdmin
      },
    },
    notificationClasses: {
      query: notificationClassesEnumValues,
      update(data) {
        console.log('APOLLO DATA -- ', data)
        return data.__type.enumValues.map((value) => value.name)
      },
    },
    searchDevices: {
      query: searchDevices,
      variables() {
        return {
          searchText: this.deviceSearchText,
          companyId: this.$store.getters['user/currentCompanyId'],
        }
      },
      skip() {
        return this.deviceSearchText === '' || this.deviceSearchText.length < 2
      },
    },
  },
  methods: {
    reset() {
      // called from parent
      this.form = {
        name: null,
        eventDetectionPointId: null,
        severity: null,
        // TODO: CHANGED TYPE
        disabled: 'DEACTIVATED',
        defaultNotificationClass: null,
        detectionMechanism: null,
        terminationMechanism: null,
        actionOnEvent: null,
        descriptionTranslations: Object.fromEntries(allowedLanguages.map((lang) => [lang, null])),
      }

      this.$v.$reset()
    },
    async triggerTestNotification() {
      if (!this.$refs.testForm.validate()) {
        return
      }

      this.testLoading = true

      try {
        const { data } = await this.$apollo.mutate({
          mutation: triggerEventNotification,
          variables: {
            deviceId: this.testForm.selectedDevice,
            eventCode: this.testForm.eventCode,
          },
        })

        if (data?.triggerEventNotification?.success) {
          this.snackbarMessage =
            data.triggerEventNotification.message || 'Event notification triggered successfully'
          this.snackbarColor = 'success'
          this.showTestDialog = false
          // Reset form
          this.testForm.eventCode = this.eventCode?.name || ''
          this.testForm.selectedDevice = null
          this.deviceSearchText = ''
        } else {
          this.snackbarMessage = data?.triggerEventNotification?.message || 'Unknown error occurred'
          this.snackbarColor = 'error'
        }
      } catch (error) {
        console.error('Error triggering test notification:', error)
        this.snackbarMessage = error.message || 'Failed to trigger notification'
        this.snackbarColor = 'error'
      } finally {
        this.testLoading = false
        this.showSnackbar = true
      }
    },
  },
  computed: {
    errorSeverities() {
      return Object.keys(errorsConfig)
    },
    allowedLanguages() {
      return allowedLanguages
    },
    activationStatus() {
      return Object.keys(eventDeactivationState)
    },
    activationStatusItems() {
      return Object.keys(eventDeactivationState).map((key) => {
        const translationMap = {
          DEACTIVATED: this.$t('global.adminEventCode.disabled'),
          ACTIVATED: this.$t('global.adminEventCode.active'),
          ELEDIO_ONLY: this.$t('global.adminEventCode.eledioOnly'),
        }
        return {
          text: translationMap[key] || key,
          value: key,
        }
      })
    },
  },
  watch: {
    eventCode: {
      handler(newEventCode) {
        if (newEventCode?.name) {
          this.testForm.eventCode = newEventCode.name
        }
      },
      immediate: true,
    },
  },
}
</script>
